import {isCallClosed} from '@/utils/callPresentation';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    AlertCircle, ArrowLeft, Boxes, CheckCircle2, ClipboardPenLine, Flag, History,
    Minus, Plus, RefreshCw, Save, Search, UserCog, Wrench,
} from 'lucide-react';
import { motion } from 'framer-motion';
import api from '@/api/axios';
import { useAuth } from '@/auth/AuthContext';
import { CallDetails, CallStatus } from '@/types/call';
import { Item, UserEntity } from '@/types/types';
import {
    CALL_PRIORITY_LABELS, CALL_PRIORITY_STYLES, CALL_STATUS_LABELS, CALL_STATUS_STYLES,
    formatCallDate, formatCallProtocol,
} from '@/utils/callPresentation';

const fieldClass = 'form-field min-h-12 text-sm';
const labelClass = 'mb-2 block text-[10px] font-black uppercase tracking-[0.16em] text-slate-400';
const operationalStatuses: CallStatus[] = ['OPENED', 'IN_PROGRESS', 'WAITING', 'CLOSED'];

interface ItemPage {
    content?: Item[];
}

const normalizeText = (value?: string | null) => (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const isMemoryItem = (item: { itemType?: { name?: string | null } | null }) => {
    const type = normalizeText(item.itemType?.name);
    return type.includes('memoria') || type.includes('memory') || type === 'ram';
};

const capacityInGb = (item: {
    specifications?: Array<{ value: string; key?: { keyName?: string | null; displayName?: string | null } | null }> | null;
}) => {
    const specification = item.specifications?.find(spec => {
        const key = normalizeText(spec.key?.keyName || spec.key?.displayName);
        return key.includes('ram_size') || key.includes('capacidade') || key.includes('tamanho');
    });
    if (!specification?.value) return 0;
    const match = specification.value.replace(',', '.').match(/[0-9]+(?:\.[0-9]+)?/);
    if (!match) return 0;
    const amount = Number(match[0]);
    const unit = specification.value.toUpperCase();
    if (unit.includes('TB')) return amount * 1024;
    if (unit.includes('MB')) return amount / 1024;
    return amount;
};

const itemName = (item: Item) => `${item.brand?.name || ''} ${item.model?.name || ''}`.trim();

const itemSpecifications = (item: Item) => (item.specifications || [])
    .map(specification => {
        const label = specification.key?.displayName || specification.key?.keyName;
        const configuredUnit = specification.key?.unit || '';
        const unit = configuredUnit && !specification.value.toUpperCase().includes(configuredUnit.toUpperCase())
            ? ` ${configuredUnit}` : '';
        return `${label}: ${specification.value}${unit}`;
    })
    .join(' · ');

export default function CallProgressPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [call, setCall] = useState<CallDetails | null>(null);
    const [technicians, setTechnicians] = useState<UserEntity[]>([]);
    const [stockItems, setStockItems] = useState<Item[]>([]);
    const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({});
    const [stockSearch, setStockSearch] = useState('');
    const [stockLoading, setStockLoading] = useState(true);
    const [stockWarning, setStockWarning] = useState('');
    const [status, setStatus] = useState<CallStatus>('OPENED');
    const [responsibleTechnicianId, setResponsibleTechnicianId] = useState('');
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (!id) return;
        let mounted = true;

        const loadPage = async () => {
            setLoading(true);
            setStockLoading(true);
            try {
                const [callResponse, technicianResponse] = await Promise.all([
                    api.get<CallDetails>(`/calls/${id}`),
                    api.get<UserEntity[]>('/users/technicians'),
                ]);
                if (!mounted) return;

                const loadedCall = callResponse.data;
                if(isCallClosed(loadedCall.status)){navigate(`/tickets/${loadedCall.id}`,{replace:true});return;}
                const loadedTechnicians = technicianResponse.data ?? [];
                setCall(loadedCall);
                setTechnicians(loadedTechnicians);
                setStatus(loadedCall.status);
                setResponsibleTechnicianId(
                    loadedCall.responsibleTechnician?.id
                    || loadedTechnicians.find(technician => technician.id === user?.id)?.id
                    || '',
                );

                if (loadedCall.equipment) {
                    try {
                        const stockResponse = await api.get<ItemPage>('/items/all', {
                            params: { status: 'STOCK', page: 0, size: 500, sort: 'model.name,asc' },
                        });
                        if (mounted) setStockItems(stockResponse.data.content ?? []);
                    } catch (stockRequestError: any) {
                        if (mounted) setStockWarning(stockRequestError?.response?.data?.message
                            || 'Não foi possível carregar as peças disponíveis. O atendimento ainda pode ser registrado sem movimentar o estoque.');
                    }
                }
            } catch (requestError: any) {
                if (mounted) setError(requestError?.response?.data?.message || 'Não foi possível carregar o chamado.');
            } finally {
                if (mounted) {
                    setLoading(false);
                    setStockLoading(false);
                }
            }
        };

        loadPage();
        return () => { mounted = false; };
    }, [id, user?.id]);

    const statusOptions = useMemo(() => {
        if (!call) return operationalStatuses;
        if (call.status === 'CLOSED' || call.status === 'CANCELED') {
            return Array.from(new Set<CallStatus>([call.status, 'OPENED', 'IN_PROGRESS']));
        }
        if (call.status === 'RESOLVED' || call.status === 'COMPLETED') {
            return Array.from(new Set<CallStatus>([call.status, 'OPENED', 'IN_PROGRESS', 'CLOSED']));
        }
        return Array.from(new Set<CallStatus>([call.status, ...operationalStatuses]));
    }, [call]);

    const filteredStockItems = useMemo(() => {
        const search = normalizeText(stockSearch.trim());
        if (!search) return stockItems;
        return stockItems.filter(item => normalizeText([
            itemName(item), item.itemType?.name, item.serialNumber, item.patrimony,
            itemSpecifications(item),
        ].filter(Boolean).join(' ')).includes(search));
    }, [stockItems, stockSearch]);

    const selectedItems = useMemo(() => stockItems
        .map(item => ({ item, quantity: selectedQuantities[item.id] || 0 }))
        .filter(line => line.quantity > 0), [selectedQuantities, stockItems]);

    const installedItems = useMemo(() => (call?.equipment?.items || [])
        .filter(item => item.status === 'INSTALLED'), [call?.equipment?.items]);

    const memoryPreview = useMemo(() => {
        const current = installedItems
            .filter(isMemoryItem)
            .reduce((total, item) => total + capacityInGb(item) * Math.max(item.quantity || 1, 1), 0);
        const added = selectedItems
            .filter(line => isMemoryItem(line.item))
            .reduce((total, line) => total + capacityInGb(line.item) * line.quantity, 0);
        return { current, added, after: current + added };
    }, [installedItems, selectedItems]);

    const changeSelectedQuantity = (item: Item, difference: number) => {
        const maximum = item.itemType?.isUnique ? Math.min(item.quantity, 1) : item.quantity;
        setSelectedQuantities(current => {
            const nextQuantity = Math.max(0, Math.min((current[item.id] || 0) + difference, maximum));
            const next = { ...current };
            if (nextQuantity === 0) delete next[item.id];
            else next[item.id] = nextQuantity;
            return next;
        });
    };

    const saveProgress = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!call) return;
        if (note.trim().length < 3) {
            setError('Relate o que foi feito com pelo menos 3 caracteres.');
            return;
        }
        setSubmitting(true);
        setError('');
        setSuccess('');
        const stockItemsPayload = selectedItems.map(line => ({ itemId: line.item.id, quantity: line.quantity }));
        try {
            const response = await api.patch<CallDetails>(`/calls/${call.id}/progress`, {
                note: note.trim(),
                status,
                responsibleTechnicianId: responsibleTechnicianId || null,
                stockItems: stockItemsPayload,
            });
            navigate('/tickets', {state:{notice:'Atendimento registrado com sucesso.'}});
            setCall(response.data);
            setStatus(response.data.status);
            setResponsibleTechnicianId(response.data.responsibleTechnician?.id || '');
            setNote('');
            setStockItems(current => current
                .map(item => ({ ...item, quantity: item.quantity - (selectedQuantities[item.id] || 0) }))
                .filter(item => item.quantity > 0));
            setSelectedQuantities({});
            setSuccess(stockItemsPayload.length > 0
                ? 'Atendimento registrado, estoque baixado e configuração do equipamento atualizada.'
                : 'Atendimento registrado e histórico atualizado com sucesso.');
        } catch (requestError: any) {
            setError(requestError?.response?.data?.message
                || requestError?.response?.data?.detail
                || 'Não foi possível registrar o atendimento.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="page-content flex min-h-[55vh] items-center justify-center gap-3 text-sm font-bold text-blue-300"><RefreshCw className="animate-spin" size={21} /> Carregando atendimento...</div>;
    }
    if (!call) {
        return <div className="page-content"><div className="feedback-error flex items-center gap-2"><AlertCircle size={18} /> {error || 'Chamado não encontrado.'}</div><button type="button" onClick={() => navigate('/tickets')} className="ui-button-secondary"><ArrowLeft size={15} /> Voltar aos chamados</button></div>;
    }

    return (
        <div className="page-content">
            <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="ui-card">
                <button type="button" onClick={() => navigate(`/tickets/${call.id}`)} className="mb-4 flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-slate-500 transition hover:text-blue-300"><ArrowLeft size={14} /> Voltar ao chamado</button>
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-xl font-black text-white">Atender chamado {formatCallProtocol(call.id)}</h2>
                            <span className={`rounded-full border px-3 py-1 text-[9px] font-black uppercase ${CALL_STATUS_STYLES[call.status]}`}>{CALL_STATUS_LABELS[call.status]}</span>
                            <span className={`rounded-full border px-3 py-1 text-[9px] font-black uppercase ${CALL_PRIORITY_STYLES[call.priority]}`}>{CALL_PRIORITY_LABELS[call.priority]}</span>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">{call.applicant?.name || 'Solicitante não informado'} · {call.subcategory?.category?.name || 'Sem categoria'} / {call.subcategory?.name || 'Sem classificação'}</p>
                    </div>
                    <div className="rounded-2xl border border-white/5 bg-[#0b0f1a] px-4 py-3 text-right">
                        <span className="block text-[9px] font-black uppercase tracking-wider text-slate-600">Aberto em</span>
                        <strong className="text-xs text-slate-300">{formatCallDate(call.openingDate)}</strong>
                    </div>
                </div>
            </motion.section>

            {error && <div className="feedback-error flex items-center gap-2"><AlertCircle size={17} /> {error}</div>}
            {success && <div className="feedback-success flex items-center justify-between gap-3"><span className="flex items-center gap-2"><CheckCircle2 size={17} /> {success}</span><button type="button" onClick={() => navigate(`/tickets/${call.id}`)} className="text-[10px] font-black uppercase tracking-wider text-emerald-200 hover:text-white">Ver chamado</button></div>}

            <div className="grid gap-6 xl:grid-cols-3">
                <motion.form onSubmit={saveProgress} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="ui-card space-y-6 xl:col-span-2">
                    <div className="border-b border-white/5 pb-4">
                        <h2 className="ui-section-title border-0 pb-0"><ClipboardPenLine size={16} className="text-blue-400" /> Registro do atendimento</h2>
                        <p className="mt-2 text-[11px] text-slate-500">O serviço, as peças utilizadas, o status e o técnico ficarão registrados no histórico.</p>
                    </div>

                    <div>
                        <label className={labelClass}>O que foi feito? *</label>
                        <textarea
                            className={`${fieldClass} min-h-36 resize-y`}
                            maxLength={1000}
                            required
                            value={note}
                            onChange={event => setNote(event.target.value)}
                            placeholder="Ex.: Substituída a fonte queimada, instalado módulo adicional de 4 GB e realizados testes de inicialização com o solicitante."
                        />
                        <p className="mt-1 text-right text-[9px] text-slate-700">{note.length}/1000</p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <label className={labelClass}><Flag size={12} className="inline" /> Novo status *</label>
                            <select className={fieldClass} value={status} onChange={event => setStatus(event.target.value as CallStatus)}>
                                {statusOptions.map(option => <option key={option} value={option}>{CALL_STATUS_LABELS[option]}{option === call.status ? ' (atual)' : ''}</option>)}
                            </select>
                            <p className="mt-1 px-1 text-[10px] text-slate-600">Fechado registra automaticamente a data de conclusão. Aberto ou em andamento reabre o chamado.</p>
                        </div>
                        <div>
                            <label className={labelClass}><UserCog size={12} className="inline" /> Técnico responsável</label>
                            <select className={fieldClass} value={responsibleTechnicianId} onChange={event => setResponsibleTechnicianId(event.target.value)}>
                                <option value="">Sem técnico atribuído</option>
                                {technicians.map(technician => <option key={technician.id} value={technician.id}>{technician.name}</option>)}
                            </select>
                            <p className="mt-1 px-1 text-[10px] text-slate-600">Selecione quem executou ou continuará executando o atendimento.</p>
                        </div>
                    </div>

                    <div className="flex flex-col-reverse gap-3 border-t border-white/5 pt-5 sm:flex-row sm:justify-end">
                        <button type="button" onClick={() => navigate(`/tickets/${call.id}`)} className="ui-button-secondary">Cancelar</button>
                        <button type="submit" disabled={submitting} className="ui-button-primary">
                            {submitting ? <><RefreshCw className="animate-spin" size={16} /> Salvando...</> : <><Save size={16} /> Registrar atendimento</>}
                        </button>
                    </div>
                </motion.form>

                    <section className="ui-card space-y-4 self-start xl:col-span-1">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <h3 className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-white"><Wrench size={16} className="text-blue-400" /> Peças utilizadas do estoque</h3>
                                <p className="mt-1 text-[10px] leading-5 text-slate-500">A baixa e a atualização do equipamento só serão efetivadas ao registrar o atendimento.</p>
                            </div>
                            {call.equipment && (
                                <div className="rounded-xl border border-blue-500/20 bg-blue-500/10 px-3 py-2 text-right">
                                    <span className="block text-[8px] font-black uppercase tracking-wider text-blue-400">Equipamento vinculado</span>
                                    <strong className="text-[11px] text-slate-200">{call.equipment.model?.brand?.name} {call.equipment.model?.name} · PAT {call.equipment.patrimony || '—'}</strong>
                                </div>
                            )}
                        </div>

                        {!call.equipment ? (
                            <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 p-4 text-xs leading-6 text-amber-300">
                                Este chamado não possui equipamento vinculado. Para retirar peças e atualizar a configuração, vincule o equipamento ao chamado.
                            </div>
                        ) : (
                            <>
                                <div className="grid gap-3 grid-cols-1">
                                    <div className="rounded-xl border border-white/5 bg-[#0b0f1a]/50 p-3">
                                        <span className="text-[9px] font-black uppercase tracking-wider text-slate-600">Configuração atual</span>
                                        {installedItems.length === 0 ? (
                                            <p className="mt-2 text-[10px] text-slate-500">Nenhum componente instalado foi cadastrado.</p>
                                        ) : (
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                {installedItems.map(item => <span key={item.id} className="rounded-lg border border-white/5 bg-white/5 px-2 py-1 text-[9px] text-slate-400">{item.itemType?.name || item.model?.type || 'Componente'}: {item.brand?.name} {item.model?.name}</span>)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="rounded-xl border border-blue-500/20 bg-blue-500/10 p-3">
                                        <span className="text-[9px] font-black uppercase tracking-wider text-blue-400">Prévia após o atendimento</span>
                                        {memoryPreview.added > 0 ? (
                                            <p className="mt-2 text-xs font-bold text-slate-200">Memória RAM: {memoryPreview.current || 0} GB → <span className="text-blue-400">{memoryPreview.after} GB</span></p>
                                        ) : (
                                            <p className="mt-2 text-[10px] text-slate-500">Selecione uma peça para visualizar a alteração.</p>
                                        )}
                                        {selectedItems.some(line => line.item.itemType?.isUnique) && <p className="mt-1 text-[9px] text-slate-500">Componentes únicos substituirão o atual, que será encaminhado para manutenção.</p>}
                                    </div>
                                </div>

                                <div className="relative">
                                    <Search size={15} className="pointer-events-none absolute left-4 top-3.5 text-slate-500" />
                                    <input className="form-field pl-10" value={stockSearch} onChange={event => setStockSearch(event.target.value)} placeholder="Buscar por peça, tipo, marca ou especificação..." />
                                </div>

                                {stockWarning && <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 p-3 text-[10px] text-amber-300">{stockWarning}</div>}
                                {stockLoading ? (
                                    <div className="flex items-center justify-center gap-2 p-8 text-xs text-slate-500"><RefreshCw size={16} className="animate-spin" /> Carregando estoque...</div>
                                ) : filteredStockItems.length === 0 ? (
                                    <div className="p-7 text-center text-xs text-slate-500">Nenhuma peça disponível encontrada.</div>
                                ) : (
                                    <div className="custom-scrollbar grid max-h-80 gap-2 overflow-y-auto pr-1 grid-cols-1">
                                        {filteredStockItems.map(item => {
                                            const selected = selectedQuantities[item.id] || 0;
                                            const maximum = item.itemType?.isUnique ? Math.min(item.quantity, 1) : item.quantity;
                                            return (
                                                <article key={item.id} className={`rounded-xl border p-3 transition ${selected > 0 ? 'border-blue-500/35 bg-blue-500/10' : 'border-white/5 bg-[#0b0f1a]/50 hover:border-blue-500/20'}`}>
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <span className="block text-[8px] font-black uppercase tracking-wider text-blue-400">{item.itemType?.name || 'Peça'}</span>
                                                            <strong className="block truncate text-[11px] text-slate-200">{itemName(item)}</strong>
                                                            <p className="mt-1 line-clamp-2 text-[9px] leading-4 text-slate-500">{itemSpecifications(item) || `S/N ${item.serialNumber || '—'} · PAT ${item.patrimony || '—'}`}</p>
                                                        </div>
                                                        <span className="shrink-0 rounded-lg bg-white/5 px-2 py-1 text-[9px] font-bold text-slate-400">Saldo {item.quantity}</span>
                                                    </div>
                                                    <div className="mt-3 flex items-center justify-between border-t border-white/5 pt-2">
                                                        <span className="text-[9px] text-slate-600">Quantidade a instalar</span>
                                                        <div className="flex items-center gap-2">
                                                            <button type="button" onClick={() => changeSelectedQuantity(item, -1)} disabled={selected === 0} className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/5 bg-white/5 text-slate-400 disabled:opacity-30"><Minus size={12} /></button>
                                                            <strong className="w-5 text-center text-xs text-white">{selected}</strong>
                                                            <button type="button" onClick={() => changeSelectedQuantity(item, 1)} disabled={selected >= maximum} className="flex h-7 w-7 items-center justify-center rounded-lg border border-blue-500/20 bg-blue-500/10 text-blue-400 disabled:opacity-30"><Plus size={12} /></button>
                                                        </div>
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                )}

                                {selectedItems.length > 0 && (
                                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3">
                                        <div className="mb-2 flex items-center gap-2 text-[9px] font-black uppercase tracking-wider text-emerald-400"><Boxes size={14} /> Peças que serão baixadas</div>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedItems.map(line => <span key={line.item.id} className="rounded-lg bg-black/10 px-2 py-1 text-[10px] text-slate-300">{line.quantity}x {itemName(line.item)}</span>)}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </section>


            </div>
                <motion.aside initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="ui-card mt-6">
                    <h2 className="ui-section-title mb-5"><History size={16} className="text-blue-400" /> Histórico do chamado</h2>
                    <div className="space-y-5">
                        {[...call.history].reverse().map(entry => (
                            <div key={entry.id} className="border-l border-blue-500/30 pl-4">
                                <div className="flex items-center justify-between gap-2"><strong className="truncate text-[10px] text-slate-300">{entry.responsibleUser?.name || 'Sistema'}</strong><span className="shrink-0 text-[9px] text-slate-700">{formatCallDate(entry.eventDate)}</span></div>
                                <p className="mt-1 text-[10px] leading-5 text-slate-500">{entry.description}</p>
                            </div>
                        ))}
                    </div>
                </motion.aside>
        </div>
    );
}
